import { useCallback, useEffect, useState } from "react";
import Map from "ol/Map";
import { useMapRotation } from "./incontext/useMapRotation";
import { degreesToRadians, radiansToDegrees } from "../utils/geometry";

/** 마우스 드래그 Y 이동량에 적용되는 각도 감도 배수 */
const DRAG_SENSITIVITY = 0.7;

/**
 * 임의의 각도 값을 [0, 360) 범위로 정규화합니다.
 */
export function normalizeRotationDegrees(degrees: number): number {
  const normalized = degrees % 360;
  return normalized < 0 ? normalized + 360 : normalized;
}

interface UseCompassRotationOptions {
  onWheel?: (degree: number) => void;
}

/**
 * CompassWheel의 회전 상태를 관리합니다.
 * - 맵 뷰 회전 변화 → 컴패스 각도 동기화
 * - 마우스 드래그 → 맵 뷰 회전 적용
 * - 리셋: 0도 복원
 */
export function useCompassRotation(
  map: Map,
  { onWheel }: UseCompassRotationOptions
) {
  const [, setRotate, resetRotation] = useMapRotation();
  const [mouseDown, setMouseDown] = useState(false);
  const [rotation, setRotation] = useState(0);

  // 맵 → 컴패스 회전 동기화
  useEffect(() => {
    const syncFromMap = () =>
      setRotation(radiansToDegrees(map.getView().getRotation()));

    syncFromMap();
    map.getView().on("change:rotation", syncFromMap);
    return () => map.getView().un("change:rotation", syncFromMap);
  }, [map]);

  const applyRotationToMap = useCallback(
    (degrees: number) => map.getView().setRotation(degreesToRadians(degrees)),
    [map]
  );

  const handleMouseDown = useCallback(() => setMouseDown(true), []);

  const handleMouseMove = useCallback(
    (e: { movementY: any }) => {
      if (!mouseDown) return;
      const delta = e.movementY * DRAG_SENSITIVITY;
      setRotation((prev) => {
        const next = normalizeRotationDegrees(prev + delta);
        applyRotationToMap(next);
        onWheel?.(next);
        setRotate(next);
        return next;
      });
    },
    [mouseDown, onWheel, setRotate, applyRotationToMap]
  );

  // 드래그 인터랙션 전역 리스너 관리
  useEffect(() => {
    const handleMouseUp = () => setMouseDown(false);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [handleMouseMove]);

  const resetValue = useCallback(() => {
    setRotation(0);
    resetRotation();
    applyRotationToMap(0);
  }, [resetRotation, applyRotationToMap]);

  return {
    rotation,
    setRotation,
    setRotate,
    applyRotationToMap,
    handleMouseDown,
    resetValue,
  };
}
